import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ProductsService } from '../../../../features/products/products.service';
import { RepackService } from '../../repack.service';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogService } from '../../../confirm-dialog/confirm-dialog.service';
import { debounceTime, Subject, switchMap, of, catchError } from 'rxjs';

@Component({
  selector: 'app-bulk-child-mapping',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './bulk-child-mapping.component.html',
  styleUrls: ['./bulk-child-mapping.component.scss'],
})
export class BulkChildMappingComponent implements OnInit {
  // Bulk Search
  bulkSearchTerm = '';
  bulkSearchResults: any[] = [];
  selectedBulkProduct: any = null;
  private bulkSearchSubject = new Subject<string>();

  // Child Search
  childSearchTerm = '';
  childSearchResults: any[] = [];
  selectedChildCandidate: any = null;
  conversionFactor: number | null = null;
  private childSearchSubject = new Subject<string>();

  // Attached Children
  attachedChildren: any[] = [];

  // Snapshot of the mapping as it currently stands on the server, used to tell
  // a real edit from just browsing. Re-taken on every load and after a save.
  private savedSnapshot = '';

  constructor(
    private productsService: ProductsService,
    private repackService: RepackService,
    private toastr: ToastrService,
    private confirmDialog: ConfirmDialogService
  ) {}

  ngOnInit(): void {
    // Setup generic search debouncers
    // No distinctUntilChanged here: the inputs are cleared programmatically
    // (on select / save) without going through the subject, so re-typing the
    // same term must still trigger a search. catchError sits inside switchMap
    // so one failed request doesn't complete the stream for good.
    this.bulkSearchSubject
      .pipe(
        debounceTime(300),
        switchMap((term) => {
          if (!term.trim()) return of([]);
          return this.productsService
            .searchByNameWithContext(term.trim(), 'REPACK_INPUT', 500)
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((results) => {
        this.bulkSearchResults = results;
      });

    this.childSearchSubject
      .pipe(
        debounceTime(300),
        switchMap((term) => {
          if (!term.trim()) return of([]);
          return this.productsService
            .searchByNameWithContext(term.trim(), 'CHILD_PRODUCTS', 500)
            .pipe(catchError(() => of([])));
        })
      )
      .subscribe((results) => {
        this.childSearchResults = results;
      });
  }

  /**
   * The name every product screen shows. The Products list renders
   * `longDescription` (that is the field the edit form writes), while
   * `Products.name` keeps whatever the SKU was first created as and drifts
   * after a rename. Fall back to `name` only when longDescription is blank.
   */
  displayName(product: any): string {
    return (
      String(product?.longDescription ?? '').trim() ||
      String(product?.name ?? '').trim() ||
      '-'
    );
  }

  /** Order-independent fingerprint of the attached-children list. */
  private snapshot(): string {
    return this.attachedChildren
      .map((c) => `${c.variantId}:${Number(c.conversionFactor) || 0}`)
      .sort()
      .join('|');
  }

  /**
   * True when leaving now would silently throw away work: either the child
   * list has been added to / removed from / re-weighted since it was loaded,
   * or a child has been picked in the Attach panel but not attached yet.
   */
  hasUnsavedChanges(): boolean {
    if (!this.selectedBulkProduct) return false;
    if (this.selectedChildCandidate || this.conversionFactor) return true;
    return this.snapshot() !== this.savedSnapshot;
  }

  /** PendingChangesGuard hook, delegated from RepackComponent. */
  async canDeactivate(): Promise<boolean> {
    if (!this.hasUnsavedChanges()) return true;
    return this.confirmDialog.confirm({
      title: 'Discard changes?',
      message:
        'You have unsaved changes in <strong>Bulk ↔ Child Mapping</strong>. Are you sure you want to leave this page?',
      confirmText: 'Leave',
      cancelText: 'Stay',
      size: 'lg',
    });
  }

  // Bulk Methods
  searchBulkProducts() {
    this.bulkSearchSubject.next(this.bulkSearchTerm);
  }

  selectBulkProduct(product: any) {
    this.selectedBulkProduct = product;
    this.resetBulkSearch();
    this.loadChildrenForBulk(product.id);
  }

  clearBulkSelection() {
    this.selectedBulkProduct = null;
    this.attachedChildren = [];
    this.savedSnapshot = '';
    this.resetBulkSearch();
    this.resetChildForm();
  }

  private resetBulkSearch() {
    this.bulkSearchTerm = '';
    this.bulkSearchResults = [];
    this.bulkSearchSubject.next('');
  }

  private resetChildForm() {
    this.selectedChildCandidate = null;
    this.childSearchTerm = '';
    this.childSearchResults = [];
    this.conversionFactor = null;
    this.childSearchSubject.next('');
  }

  loadChildrenForBulk(bulkId: string) {
    this.repackService.getChildProducts(bulkId).subscribe({
      next: (res: any) => {
        let children = [];
        if (Array.isArray(res)) {
          children = res;
        } else if (res.data && Array.isArray(res.data)) {
          children = res.data;
        } else if (res.children) {
          children = res.children;
        }

        this.attachedChildren = children.map((item: any) => ({
          variantId: item.variants?.[0]?.id || item.id,
          sku: item.variants?.[0]?.sku || item.sku,
          name: this.displayName(item),
          conversionFactor: item.conversionFactor || 0,
          originalItem: item,
        }));
        this.savedSnapshot = this.snapshot();
      },
      error: (err) => {
        console.error('Error loading children', err);
      },
    });
  }

  // Child Methods
  searchChildProducts() {
    this.childSearchSubject.next(this.childSearchTerm);
  }

  selectChildCandidate(product: any) {
    this.selectedChildCandidate = product;
    this.childSearchResults = [];
    this.childSearchTerm = this.displayName(product); // Show selected name
    this.childSearchSubject.next('');
  }

  attachChild() {
    if (!this.selectedChildCandidate || !this.conversionFactor) return;

    // Search results are Products rows; the mapping API wants the
    // ProductVariant id (same as loadChildrenForBulk uses).
    const variantId =
      this.selectedChildCandidate.variants?.[0]?.id ||
      this.selectedChildCandidate.variantId;
    if (!variantId) {
      this.toastr.error('Selected product has no variant to map');
      return;
    }

    // Check if already added
    const existing = this.attachedChildren.find(
      (c) => c.variantId === variantId
    );
    if (existing) {
      this.toastr.warning('Product already attached');
      return;
    }

    this.attachedChildren.push({
      variantId,
      sku: this.selectedChildCandidate.sku,
      name: this.displayName(this.selectedChildCandidate), // Display purpose, might not be persisted
      conversionFactor: this.conversionFactor,
    });

    this.resetChildForm();
  }

  removeChild(child: any) {
    this.attachedChildren = this.attachedChildren.filter((c) => c !== child);
  }

  async saveMapping() {
    if (!this.selectedBulkProduct) return;

    const bad = this.attachedChildren.find(
      (c) => !c.conversionFactor || Number(c.conversionFactor) <= 0
    );
    if (bad) {
      this.toastr.warning(`Enter a conversion (grams) for ${bad.name}`);
      return;
    }

    const count = this.attachedChildren.length;
    const proceed = await this.confirmDialog.confirm({
      title: 'Save mapping?',
      message:
        count === 0
          ? `This will remove all child mappings from ${this.displayName(this.selectedBulkProduct)} (${this.selectedBulkProduct.sku}).`
          : `Map ${count} child product${count === 1 ? '' : 's'} to ${this.displayName(this.selectedBulkProduct)} (${this.selectedBulkProduct.sku})? Existing mappings not in this list will be removed.`,
      confirmText: 'Save',
      cancelText: 'Cancel',
    });
    if (!proceed) return;

    const payload = {
      children: this.attachedChildren.map((c) => ({
        variantId: c.variantId,
        conversionFactor: Number(c.conversionFactor),
      })),
    };

    this.repackService
      .saveBulkMapping(this.selectedBulkProduct.id, payload)
      .subscribe({
        next: () => {
          this.toastr.success('Mapping saved successfully!');
          this.clearBulkSelection(); // Reset the page
        },
        error: (err) => {
          console.error('Error saving mapping', err);
          this.toastr.error(err?.error?.message || 'Failed to save mapping');
        },
      });
  }
}
